import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'

import { AgendaDelTecnico } from './AgendaDelTecnico'
import { cuandoEs, DURACIONES, duracion, hoyEnColombia, instante, partes } from './horario'
import { useProgramarVisita } from './useVisitas'
import estilos from './ProgramarVisita.module.css'

/**
 * QUIÉN VA, QUÉ DÍA Y A QUÉ HORA (0256).
 *
 * Antes era un selector de «Va a cotizar»: decía quién, pero la visita no
 * tenía fecha y no salía en ningún calendario. Johan: «cuando se elige un
 * técnico debería ser como programación también». Ahora elegir al técnico es
 * programarle la visita: día, hora y cuánto dura, viendo lo que ya tiene ese
 * día. Sale en su teléfono, en su programación y en el calendario de la
 * oficina.
 */

type Tecnico = { id: string; nombre: string }

function useTecnicos() {
  const consulta = useQuery({
    queryKey: ['colaboradores', 'para-cotizar'],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Tecnico[]> => {
      const { data, error } = await supabase
        .from('colaboradores')
        .select('id, nombre')
        .eq('activo', true)
        .order('nombre')
      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as Tecnico[]
    },
  })
  return consulta.data ?? []
}

export function ProgramarVisita({
  ordenServicioId,
  colaboradorId,
  nombreActual,
  inicioActual,
  horasActual,
  puedeEditar,
}: {
  ordenServicioId: string
  colaboradorId: string | null
  nombreActual: string | null
  inicioActual: string | null
  horasActual: number | null
  puedeEditar: boolean
}) {
  const tecnicos = useTecnicos()
  const programar = useProgramarVisita()
  const ya = inicioActual ? partes(inicioActual) : null

  const [editando, setEditando] = useState(false)
  const [quien, setQuien] = useState(colaboradorId ?? '')
  const [fecha, setFecha] = useState(ya?.fecha ?? hoyEnColombia())
  const [hora, setHora] = useState(ya?.hora ?? '08:00')
  const [horas, setHoras] = useState(Number(horasActual ?? 1))

  const nombreDe = (id: string) => tecnicos.find((t) => t.id === id)?.nombre ?? ''
  const inicio = fecha && hora ? new Date(instante(fecha, hora)) : null
  const fin = inicio ? new Date(inicio.getTime() + horas * 3_600_000) : null

  function abrir() {
    setQuien(colaboradorId ?? '')
    setEditando(true)
    programar.reset()
  }

  async function guardar() {
    await programar.mutateAsync({
      ordenServicioId,
      colaboradorId: quien,
      inicio: instante(fecha, hora),
      horas,
    })
    setEditando(false)
  }

  async function quitar() {
    await programar.mutateAsync({ ordenServicioId, colaboradorId: null, inicio: null, horas: 1 })
    setEditando(false)
  }

  // ── Lo que hay, dicho en una línea ─────────────────────────────────────
  if (!editando) {
    return (
      <div className={estilos.zona}>
        <span className={estilos.rotulo}>Visita</span>
        {colaboradorId ? (
          <>
            <p className={estilos.quien}>{nombreActual}</p>
            {inicioActual ? (
              <p className={estilos.cuando}>
                {cuandoEs(inicioActual)} · {duracion(horasActual)}
              </p>
            ) : (
              <p className={estilos.sinFecha}>
                Sin fecha: no sale en ningún calendario. Póngale día y hora.
              </p>
            )}
            <p className={estilos.ayuda}>
              Le aparece en su teléfono y en su programación; desde la obra la puede cotizar.
            </p>
          </>
        ) : (
          <p className={estilos.ayuda}>Nadie va todavía. Escoja quién va, qué día y a qué hora.</p>
        )}
        {puedeEditar && (
          <div className={estilos.botones}>
            <button type="button" className={estilos.principal} onClick={abrir}>
              {!colaboradorId ? 'Programar visita' : inicioActual ? 'Cambiar' : 'Ponerle fecha'}
            </button>
            {colaboradorId && (
              <button
                type="button"
                className={estilos.texto}
                onClick={() => void quitar()}
                disabled={programar.isPending}
              >
                Quitar visita
              </button>
            )}
          </div>
        )}
        {programar.error && <p className={estilos.error}>{programar.error.message}</p>}
      </div>
    )
  }

  // ── Programarla ────────────────────────────────────────────────────────
  return (
    <div className={`${estilos.zona} ${estilos.zonaAbierta}`}>
      <span className={estilos.rotulo}>Programar la visita</span>

      <div className={estilos.campos}>
        <label className={estilos.campoAncho}>
          <span>Técnico</span>
          <select value={quien} onChange={(e) => setQuien(e.target.value)}>
            <option value="">Escoja…</option>
            {tecnicos.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nombre}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Día</span>
          <input
            type="date"
            value={fecha}
            min={hoyEnColombia()}
            onChange={(e) => setFecha(e.target.value)}
          />
        </label>
        <label>
          <span>Hora</span>
          <input type="time" value={hora} step={900} onChange={(e) => setHora(e.target.value)} />
        </label>
        <label>
          <span>Dura</span>
          <select value={horas} onChange={(e) => setHoras(Number(e.target.value))}>
            {DURACIONES.map((h) => (
              <option key={h} value={h}>
                {duracion(h)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {quien && fecha && (
        <AgendaDelTecnico
          colaboradorId={quien}
          nombre={nombreDe(quien)}
          fecha={fecha}
          inicio={inicio}
          fin={fin}
          sinContar={ordenServicioId}
        />
      )}

      {programar.error && <p className={estilos.error}>{programar.error.message}</p>}

      <div className={estilos.botones}>
        <button
          type="button"
          className={estilos.principal}
          onClick={() => void guardar()}
          disabled={!quien || !fecha || !hora || programar.isPending}
        >
          {programar.isPending ? 'Programando…' : 'Programar visita'}
        </button>
        <button type="button" className={estilos.texto} onClick={() => setEditando(false)}>
          Cancelar
        </button>
      </div>
    </div>
  )
}
